import { TFunction } from "i18next";
import { CertificateType, Check } from "./Certificate";
import { Progress } from "./RegisterProgress";
import { combineSentence, splitSentence } from "../util/textUtil";


export interface ProcessedProgress {
  certificateType: CertificateType;
  checks: Check[];
}

export type SimplifiedProgress = Record<string, any>

export const simplifyProgress = (progress: Progress): SimplifiedProgress => {
  if (!progress || !progress.registrationChunks) {
    return undefined;
  }
  // Sort registrationChunks by startTimeMs in descending order
  const sortedChunks = progress.registrationChunks.sort((a, b) => b.startTimeMs - a.startTimeMs);
  const simple: Record<string, any> = {}

  // Iterate over the sorted registrationChunks
  for (const chunk of sortedChunks) {
    Object.entries(chunk.changes).forEach(([key, value]) => {
      if (!(key in simple)) {
        simple[key] = value;
      }
    });
  }
  return simple;
}

export enum ProgressSection {
  VALUES = "values",
  SPREAD_THE_WORD = "spread-the-word",
  SHARE = "share",
  LEARN = "learn",
  PURCHASES = "purchases",
  SERVICES = "services",
  UNSPECIFIED = "unspecified" // special section used when opening
}

export const getDefaultChecks: (t: TFunction<"translation", undefined>) => Check[] = (t) => {
  return [
    new Check(false, t('challenge.task.reflect-on-values')),
    new Check(false, t('challenge.task.learn-about-alternatives')),
    new Check(false, t('challenge.task.change-purchasing-habits')),
    new Check(false, t('challenge.task.reconsider-online-services')),
    new Check(false, t('challenge.task.spread-the-word')),
    new Check(false, t('challenge.task.share-the-challenge')),
  ];
}

export const checkedCount = (pp: ProcessedProgress) => {
  return pp ? pp.checks.reduce((acc, v) => acc + (v.checked ? 1 : 0), 0) : 0;
}

export const getProcessedProgress = (
  pr: SimplifiedProgress,
  referralCount: number,
  daysSinceJoined: number,
  valueKeysAndLabels,
  spreadKeysAndLabels,
  shareKeysAndLabels,
  t: TFunction<"translation", undefined>
): ProcessedProgress => {
  const valueCheck = checkFromSimplifiedProgress(pr, valueKeysAndLabels, t,
    "challenge.value.custom", t("challenge.cert.reflect-on-values.prefix.plural"),
    t("challenge.cert.reflect-on-values.prefix.plural"), t('challenge.task.reflect-on-values'), ProgressSection.VALUES
  );
  const spreadCheck = checkFromSimplifiedProgress(pr, spreadKeysAndLabels, t, 
    "challenge.spread-the-word.custom", t('challenge.cert.spread-the-word.prefix.plural'),
    t('challenge.cert.spread-the-word.prefix.singular'), t("challenge.task.spread-the-word"), ProgressSection.SPREAD_THE_WORD
  );
  const shareCheck = checkFromSimplifiedProgress(pr, shareKeysAndLabels, t, 
    "challenge.share.custom", t('challenge.cert.share-the-challenge.prefix'),
    t('challenge.cert.share-the-challenge.prefix'), t("challenge.task.share-the-challenge"), ProgressSection.SHARE
  );
  const referralCheck: Check = !shareCheck.checked ? undefined
    : new Check(referralCount > 0, t('challenge.task.people-joined-from-sharing', { count: referralCount }))

  const checks: Check[] = [
    valueCheck,
    learnCheckFromSimplifiedProgress(pr, t),
    changePurchasingCheckFromSimplifiedProgress(pr, t),
    reconsiderServicesCheckFromSimplifiedProgress(pr, t),
    spreadCheck, shareCheck, referralCheck,
  ].filter((e) => !!e);


  const prerunCheckedCount = checks.reduce((acc, v) => acc + (v.checked ? 1 : 0), 0);

  if (prerunCheckedCount >= 6) {
    checks.push(new Check(daysSinceJoined > 0,
      t('challenge.task.joined-days-ago', { count: daysSinceJoined })
    ))
  }

  const checkedCount = checks.reduce((acc, v) => acc + (v.checked ? 1 : 0), 0);

  const certificateType = checkedCount >= 8
    ? CertificateType.GOLD
    : checkedCount >= 6
    ? CertificateType.SILVER
    : checkedCount >= 2
    ? CertificateType.BRONZE
    : CertificateType.GREEN;

  return {
    certificateType,
    checks
  }
  
}

export abstract class AdjustableText {
  t: any;
  text: string;
  constructor(t) {
    this.t = t;
  }

  /**
   * Example: sizeFactor 0.9 should try to reduce number of chars by 10%
   * @param sizeFactor try to adjust the length of the text by this factor
   */
  abstract adjust(sizeFactor: number);

  /** Adjust the text length down to the next natural form. Typically by removed one item. */
  abstract adjustDown();
}

const checkFromSimplifiedProgress = (
  pr, keysAndLabels, t,
  customKey, pluralPrefix, singularPrefix: string, defaultText,
  checkKey: ProgressSection
): Check => {
  const items: string[] = [];
  if (pr) {
    keysAndLabels.forEach(([key, label]) => {
      if (pr[key]) {
        items.push(label);
      }
    });
    const custom = pr[customKey] as string;
    if (custom) {
      const customItems = splitSentence(custom);
      for (let citem of customItems) {
        items.push(citem);
      }
    }
  }
  return new Check(items.length > 0, new ItemAdjustableText(
    singularPrefix,
    pluralPrefix,
    keysAndLabels,
    t,
    defaultText,
    items,
  ), checkKey);
}

class ItemAdjustableText extends AdjustableText {
  singularPrefix: any;
  pluralPrefix: any;
  keysAndLabels: any;
  defaultText: any;
  items: string[];
  itemKeep: number;
  constructor(singularPrefix, pluralPrefix, keysAndLabels, t, defaultText, items) {
    super(t);
    this.singularPrefix = singularPrefix;
    this.pluralPrefix = pluralPrefix;
    this.keysAndLabels = keysAndLabels;
    this.defaultText = defaultText;
    this.items = items;
    this.itemKeep = items.length;
    this.build();
  }

  adjust(sizeFactor: number) {
    const orgLength = this.text.length;
    do {
      this.adjustDown()
      if (this.itemKeep === 0) {
        break;
      }
    } while(this.text.length / orgLength > sizeFactor);
  }

  adjustDown() {
    if (this.itemKeep > 0) {
      this.itemKeep -= 1;
      this.build();
    }
  }

  build() {
    if (this.itemKeep === 0) {
      this.text = this.defaultText;
      return;
    }
    const keptItems = [...this.items.slice(0, this.itemKeep)];
    const order = (label) => {
      const v = this.keysAndLabels.find((v) => v[1] === label);
      if (v) {
        return v[2];
      }
      return 1;
    }
    keptItems.sort((a, b) => order(a) - order(b));
    const conjunction = this.t('conjunction-and');
    this.text = (this.itemKeep === 1 ? this.singularPrefix : this.pluralPrefix).trim() + " "
      + combineSentence(keptItems, conjunction, conjunction === "and");
  }
}

const learnCheckFromSimplifiedProgress = (
  pr, t,
): Check => {
  const checkKey = ProgressSection.LEARN;
  // Learned these are European: ProductA, productB, and brand; these are not: ProductC and productD.
  var text: DoubleItemListAdjustableText = undefined;
  var checked = false;
  if (pr) {
    const european = pr["challenge.learn.european"]
    const notEuropean = pr["challenge.learn.not-european"]
    text = new DoubleItemListAdjustableText(t,
      european ? splitSentence(european) : [],
      notEuropean ? splitSentence(notEuropean) : [],
      (t, items1, items2) => t('challenge.cert.learn-about-alternatives.both', { european: items1, notEuropean: items2 }),
      (t, items) => t('challenge.cert.learn-about-alternatives.european', { european: items }),
      (t, items) => t('challenge.cert.learn-about-alternatives.not-european', { notEuropean: items }),
      t('challenge.task.learn-about-alternatives')
    );
    checked = text.firstItems.length > 0 || text.secondItems.length > 0;
  }
  return new Check(checked, text || t('challenge.task.learn-about-alternatives'), checkKey)
}


export class LearnAdjustableText extends AdjustableText {
  euroItems: string[];
  notEuroItems: string[];
  euroKeep: number;
  notEuroKeep: number;
  useEuroNext: boolean;
  constructor(t, euroItems, notEuroItems) {
    super(t);
    this.euroItems = euroItems;
    this.notEuroItems = notEuroItems;
    this.useEuroNext = euroItems.length > notEuroItems.length;
    this.euroKeep = euroItems.length;
    this.notEuroKeep = notEuroItems.length;
    this.build()
  }

  adjust(sizeFactor: number) {
    const orgLength = this.text.length;
    do {
      this.adjustDown()
      if (this.euroKeep === 0 && this.notEuroKeep === 0) {
        break;
      }
    } while(this.text.length / orgLength > sizeFactor);
  }

  adjustDown() {
    if (this.euroKeep <= 1) {
      if (this.notEuroKeep === 0) {
        this.euroKeep = 0;
      } else if (this.notEuroKeep === 1) {
        if (this.euroKeep === 0 || this.euroItems.length > this.notEuroItems.length) {
          this.notEuroKeep = 0;
        } else {
          this.euroKeep = 0;
        }
      } else {
        this.notEuroKeep -= 1;
      }
      this.build();
      return;
    }
    if (this.notEuroKeep <= 1) {
      this.euroKeep -= 1;
      this.build();
      return;
    }
    const orgRatio = this.euroItems.length / this.notEuroItems.length;
    const remEuroRatio = (this.euroKeep - 1) / this.notEuroKeep;
    const remNotEuroRatio = this.euroKeep / (this.notEuroKeep - 1);
    if (Math.abs(remEuroRatio - orgRatio) < Math.abs(remNotEuroRatio - orgRatio)) {
      this.euroKeep -= 1;
    } else {
      this.notEuroKeep -= 1;
    }
    this.build();
  }

  build() {
    const conjunction = this.t('conjunction-and');
    const european = combineSentence(this.euroItems.slice(0, this.euroKeep), conjunction, conjunction === "and");
    const notEuropean = combineSentence(this.notEuroItems.slice(0, this.notEuroKeep), conjunction, conjunction === "and");
    if (european && notEuropean) {
      this.text = this.t("challenge.cert.learn-about-alternatives.both", { european, notEuropean })
    } else if (european) {
      this.text = this.t('challenge.cert.learn-about-alternatives.european', { european })
    } else if (notEuropean) {
      this.text = this.t('challenge.cert.learn-about-alternatives.not-european', { notEuropean })
    } else {
      this.text = this.t('challenge.task.learn-about-alternatives');
    }
  }
}

const changePurchasingCheckFromSimplifiedProgress = (
  pr, t,
): Check => {
  const checkKey = ProgressSection.PURCHASES;
  // Started buying: ProductA, productB, and brand; stopped buying: ProductC and productD.
  var text: DoubleItemListAdjustableText = undefined;
  var checked = false;
  if (pr) {
    const started = pr["challenge.change-purchasing.started"]
    const stopped = pr["challenge.change-purchasing.stopped"]
    text = new DoubleItemListAdjustableText(t,
      started ? splitSentence(started) : [],
      stopped ? splitSentence(stopped) : [],
      (t, items1, items2) => t('challenge.cert.change-purchasing-habits.both', { started: items1, stopped: items2 }),
      (t, items) => t('challenge.cert.change-purchasing-habits.started', { started: items }),
      (t, items) => t('challenge.cert.change-purchasing-habits.stopped', { stopped: items }),
      t('challenge.task.change-purchasing-habits')
    );
    checked = text.firstItems.length > 0 || text.secondItems.length > 0;
  }
  return new Check(checked, text || t('challenge.task.change-purchasing-habits'), checkKey)
}

const reconsiderServicesCheckFromSimplifiedProgress = (
  pr, t,
): Check => {
  const checkKey = ProgressSection.SERVICES;
  var text: DoubleItemListAdjustableText = undefined;
  var checked = false;
  if (pr) {
    const started = pr["challenge.reconsider-services.signed-up-with"]
    const stopped = pr["challenge.reconsider-services.reduced-or-cancelled"]
    text = new DoubleItemListAdjustableText(t,
      started ? splitSentence(started) : [],
      stopped ? splitSentence(stopped) : [],
      (t, items1, items2) => t('challenge.cert.reconsider-online-services.both', { signedUpWith: items1, reducedOrCancelled: items2 }),
      (t, items) => t('challenge.cert.reconsider-online-services.signed-up-with', { signedUpWith: items }),
      (t, items) => t('challenge.cert.reconsider-online-services.reduced-or-cancelled', { reducedOrCancelled: items }),
      t('challenge.task.reconsider-online-services')
    );
    checked = text.firstItems.length > 0 || text.secondItems.length > 0;
  }
  return new Check(checked, text || t('challenge.task.reconsider-online-services'), checkKey)
}

export class DoubleItemListAdjustableText extends AdjustableText {
  firstItems: string[];
  secondItems: string[];
  firstKeep: number;
  secondKeep: number;
  bothToText: (t, items1, items2) => string;
  firstToText: (t, items) => string;
  secondToText: (t, items) => string;
  defaultText: string;
  constructor(t, firstItems, secondItems, bothToText, firstToText, secondToText, defaultText) {
    super(t);
    this.firstItems = firstItems;
    this.secondItems = secondItems;
    this.firstKeep = firstItems.length;
    this.secondKeep = secondItems.length;
    this.bothToText = bothToText;
    this.firstToText = firstToText;
    this.secondToText = secondToText;
    this.defaultText = defaultText;
    this.build()
  }

  adjust(sizeFactor: number) {
    const orgLength = this.text.length;
    do {
      this.adjustDown()
      if (this.firstKeep === 0 && this.secondKeep === 0) {
        break;
      }
    } while(this.text.length / orgLength > sizeFactor);
  }

  adjustDown() {
    if (this.firstKeep <= 1) {
      if (this.secondKeep === 0) {
        this.firstKeep = 0;
      } else if (this.secondKeep === 1) {
        if (this.firstKeep === 0 || this.firstItems.length > this.secondItems.length) {
          this.secondKeep = 0;
        } else {
          this.firstKeep = 0;
        }
      } else {
        this.secondKeep -= 1;
      }
      this.build();
      return;
    }
    if (this.secondKeep <= 1) {
      this.firstKeep -= 1;
      this.build();
      return;
    }
    const orgRatio = this.firstItems.length / this.secondItems.length;
    const remFirstRatio = (this.firstKeep - 1) / this.secondKeep;
    const remSecondRatio = this.firstKeep / (this.secondKeep - 1);
    if (Math.abs(remFirstRatio - orgRatio) < Math.abs(remSecondRatio - orgRatio)) {
      this.firstKeep -= 1;
    } else {
      this.secondKeep -= 1;
    }
    this.build();
  }

  build() {
    const conjunction = this.t('conjunction-and');
    const items1 = combineSentence(this.firstItems.slice(0, this.firstKeep), conjunction, conjunction === "and");
    const items2 = combineSentence(this.secondItems.slice(0, this.secondKeep), conjunction, conjunction === "and");
    if (items1 && items2) {
      this.text = this.bothToText(this.t, items1, items2);
      //this.t("challenge.cert.learn-about-alternatives.both", { items1, items2 })
    } else if (items1) {
      this.text = this.firstToText(this.t, items1);
      //this.text = this.t('challenge.cert.learn-about-alternatives.european', { items: items1 })
    } else if (items2) {
      this.text = this.secondToText(this.t, items2);
      //this.text = this.t('challenge.cert.learn-about-alternatives.not-european', { items: items2 })
    } else {
      this.text = this.defaultText;
    }
  }
}
